import { z } from 'zod'

// ISSUE #19: Memory write/search API のための共有 Zod schema 群。
//
// 仕様書 (Phase 3 §3.2 / §6.2 / §6.4 / §16) の memories テーブルに insert される
// memory の 10 種別と provenance 用 origin の 6 種別を厳格に定義する。
//
// 重要: `memories.kind` enum は `skill_candidate` を **含まない**。
// `skill_candidate` は Learning Extractor が抽出する候補種別の 1 つだが、
// memories テーブルではなく Skill Registry (Phase 6) に振り分ける目印として
// `learning.ts` の `ExtractedCandidateKindSchema` 側で別 enum として扱う。

// §6.2 の 10 種。memories テーブルの `kind` 列にそのまま入る値。
export const MemoryKindSchema = z.enum([
  'user_preference',
  'project_fact',
  'decision',
  'workflow_rule',
  'failure_pattern',
  'security_caution',
  'tool_usage_note',
  'environment_fact',
  'temporary_context',
  'deprecated_fact',
])
export type MemoryKind = z.infer<typeof MemoryKindSchema>

// §3.2.1 の origin 6 種。`external-*` の memory は必ず status='candidate' で入る
// (auto-trusted 化禁止) ためルートとサービス層で個別に validate する。
export const MemoryOriginSchema = z.enum([
  'agent-self',
  'user-explicit',
  'external-web',
  'external-issue',
  'external-slack',
  'extracted-from-execution',
])
export type MemoryOrigin = z.infer<typeof MemoryOriginSchema>

// `external-*` 判定用のヘルパー定数。Worker 側 services/memories.ts でも参照。
export const EXTERNAL_ORIGINS = [
  'external-web',
  'external-issue',
  'external-slack',
] as const satisfies readonly MemoryOrigin[]

export type ExternalOrigin = (typeof EXTERNAL_ORIGINS)[number]

// memories.status (D1 schema 由来。`promoted` / `archived` は将来追加予定)。
//
// `quarantined` は ISSUE #23 の Memory Manager UI で reject ボタンを押した時の
// 受け皿。`rejected` は Learning Extractor (#20) が safety_score 低い候補を
// 自動弾きする時に使う、という暗黙的な棲み分け。
export const MemoryStatusSchema = z.enum([
  'candidate',
  'trusted',
  'rejected',
  'quarantined',
  'expired',
])
export type MemoryStatus = z.infer<typeof MemoryStatusSchema>

// Attestation chain (§3.2.1)。memory ごとに最低 1 ステップを持つ。
export const AttestationStepSchema = z.object({
  step: z.enum(['raw_capture', 'extract', 'candidate_insert', 'approve', 'promote']),
  at: z.string(),
  signer: z.enum(['agent', 'user']),
  evidence_r2_key: z.string().optional(),
  job_id: z.string().optional(),
})
export type AttestationStep = z.infer<typeof AttestationStepSchema>

export const AttestationChainSchema = z.array(AttestationStepSchema).min(1)
export type AttestationChain = z.infer<typeof AttestationChainSchema>

// POST /api/memories の入力。
//
// origin が `external-*` の場合は POST handler 側で status='candidate' を強制し、
// trust_score / utility_score / safety_score 等は default 値を入れる。
// (将来 `status: 'trusted'` で投げてきても無視する。)
export const CreateMemoryRequestSchema = z.object({
  summary: z.string().min(1).max(2000),
  kind: MemoryKindSchema,
  origin: MemoryOriginSchema,
  source: z.string().min(1).max(255),
  source_uri: z.string().url().optional(),
  project: z.string().min(1).max(255).optional(),
  raw_evidence_r2_key: z.string().min(1).max(512).optional(),
  ingestion_path: z.string().min(1).max(255).optional(),
  // user-explicit など内部 origin で投げる場合は status を明示できる。
  // external origin の場合は service 層で `candidate` に上書きされる。
  status: MemoryStatusSchema.optional(),
  // attestation chain は最低 1 ステップ。route handler では user JWT identity から
  // `{ step: 'candidate_insert', signer: 'user' }` を自動補完するが、internal 呼び出し
  // (#20 Learning Extractor) では明示的に渡す。
  attestation_chain: AttestationChainSchema.optional(),
  // 各種 score (default は migration の DEFAULT 値が効くので optional)
  trust_score: z.number().min(0).max(1).optional(),
  utility_score: z.number().min(0).max(1).optional(),
  safety_score: z.number().min(0).max(1).optional(),
  source_reliability: z.number().min(0).max(1).optional(),
  stale_after: z.string().optional(),
})
export type CreateMemoryRequest = z.infer<typeof CreateMemoryRequestSchema>

// Memory の row 表現 (Worker -> UI / 他 service)。
export const MemorySchema = z.object({
  id: z.string(),
  status: MemoryStatusSchema,
  kind: MemoryKindSchema,
  project: z.string().nullable(),
  source: z.string(),
  source_uri: z.string().nullable(),
  summary: z.string(),
  ai_search_item_id: z.string().nullable(),
  raw_evidence_r2_key: z.string().nullable(),
  origin: MemoryOriginSchema,
  origin_signature: z.string().nullable(),
  ingestion_path: z.string().nullable(),
  attestation_chain: AttestationChainSchema.nullable(),
  trust_score: z.number(),
  utility_score: z.number(),
  safety_score: z.number(),
  source_reliability: z.number(),
  retrieval_count: z.number().int(),
  helpful_count: z.number().int(),
  harmful_count: z.number().int(),
  conflict_group_id: z.string().nullable(),
  stale_after: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})
export type Memory = z.infer<typeof MemorySchema>

// GET /api/memories/search の response shape。`final_score` は Worker 側で計算した値。
export const MemorySearchHitSchema = MemorySchema.extend({
  search_score: z.number(),
  final_score: z.number(),
})
export type MemorySearchHit = z.infer<typeof MemorySearchHitSchema>

export const MemorySearchResponseSchema = z.object({
  memories: z.array(MemorySearchHitSchema),
})
export type MemorySearchResponse = z.infer<typeof MemorySearchResponseSchema>

// ISSUE #23 (Memory Manager UI): status filter で D1 から直接 list する endpoint の
// response shape。AI Search は使わずに D1 のみ参照するので `search_score` /
// `final_score` は無し。
export const MemoryListResponseSchema = z.object({
  memories: z.array(MemorySchema),
})
export type MemoryListResponse = z.infer<typeof MemoryListResponseSchema>

// ISSUE #23: approve / reject の response shape。
export const MemoryDecisionResponseSchema = z.object({
  id: z.string(),
  status: MemoryStatusSchema,
})
export type MemoryDecisionResponse = z.infer<typeof MemoryDecisionResponseSchema>
